import { createTheme } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import isNil from "lodash/isNil";

/**
 * MUI theme.
 *
 * Palette: NAVY primary (buttons/links), SLATE secondary, a custom AMBER
 * accent (`color="amber"` on Button/Chip), and a TEAL brand color used by the
 * app bar / top header. Status colors (success/warning/error/info) are kept
 * and also expose `.fill` / `.border` for chips.
 *
 * `primaryColor` / `secondaryColor` remain honored as overrides (partner
 * config), defaulting to navy / slate.
 */

// ── Brand / palette ──────────────────────────────────────────────────────
const NAVY_MAIN = "#1B3A4B";
const NAVY_LIGHT = "#36586B";
const NAVY_DARK = "#10222D";

const SLATE_MAIN = "#64748B";
const SLATE_LIGHT = "#94A3B8";
const SLATE_DARK = "#475569";

const AMBER_MAIN = "#E08A1E";
const AMBER_LIGHT = "#FAEEDA";
const AMBER_DARK = "#B86F11";
const AMBER_CHIP_TEXT = "#854F0B";

// Lendmarq brand blue — app bar / top header + control accents.
const TEAL_BRAND = "#1F5AF2";
const FOCUS_RING = "0 0 0 3px rgba(31,90,242,.18)";

// ── Neutral ramp / surfaces ──────────────────────────────────────────────
const INK = "#1B2733"; // primary text
const MUTED = "#64748B"; // secondary text
const FAINT = "#94A3B8"; // disabled / hints
const LINE = "#E2E8F0"; // borders / divider
const LINE_SOFT = "#eef2f6"; // inner dividers
const LINE_HOVER = "#cbd5e1"; // hover / emphasis border
const CANVAS = "#F8FAFC"; // page background
const SURFACE = "#FFFFFF"; // cards, panels

// ── Status colors — dark text on matching light fill ─────────────────────
const STATUS = {
  success: { main: "#047857", fill: "#ecfdf5", border: "#a7f3d0" },
  warning: { main: "#b45309", fill: "#fffbeb", border: "#fde68a" },
  error: { main: "#be123c", fill: "#fff1f2", border: "#fecdd3" },
  info: { main: SLATE_MAIN, fill: "#f1f5f9", border: LINE },
};

// ── Type, radius, elevation ──────────────────────────────────────────────
const FONT_FAMILY = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';
const CONTROL_RADIUS = 8; // buttons, inputs, chips
const CARD_RADIUS = 14; // cards / panels
const CARD_SHADOW = "0 1px 3px rgba(15,23,42,.05)";
const MODAL_SHADOW = "0 8px 28px rgba(15,23,42,.16)";

// Compact "pill" sizing shared by the `ghost` + `solid` Button variants so they
// line up identically in a toolbar — only the color treatment differs.
const PILL_BUTTON_SIZING = {
  fontSize: "12.5px",
  fontWeight: 570,
  gap: 7,
  padding: "7px 13px",
  borderRadius: 8,
  whiteSpace: "nowrap",
  transition: "all 0.15s ease",
  // gap owns icon spacing, so zero MUI's default start/end margins.
  "& .MuiButton-startIcon, & .MuiButton-endIcon": { margin: 0 },
  "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": { fontSize: 16 },
};

const inputDefaultProps = {
  size: "small",
  variant: "outlined",
};

// Ghost (transparent / on-dark) input variant — kept for map search overlays.
const GHOST_COLOR = "#ffffff";
const GHOST_BORDER = "rgba(255, 255, 255, 0.6)";
const GHOST_BORDER_HOVER = "rgba(255, 255, 255, 0.85)";
const GHOST_BORDER_FOCUS = "#ffffff";
const GHOST_PLACEHOLDER = "rgba(255, 255, 255, 0.6)";
const GHOST_DISABLED = "rgba(255, 255, 255, 0.35)";

const generateMuiThemeDefaults = ({
  mode,
  primaryColor,
  secondaryColor,
} = {}) => {
  return createTheme({
    shape: { borderRadius: CONTROL_RADIUS },

    // The drilldown sidebars are custom fixed panels stacked well above MUI's
    // default scale (pane 13000, icon rail 13001, detail sidebars 13002). MUI's
    // default modal layer (Popover / Menu / Select / Autocomplete / Dialog) is
    // only 1300, so any dropdown opened INSIDE a sidebar would render behind it.
    // Lift the whole overlay scale above the sidebars so popovers/menus always
    // float on top, preserving MUI's relative order (modal < snackbar < tooltip).
    zIndex: {
      mobileStepper: 1000,
      fab: 1050,
      speedDial: 1050,
      appBar: 1100,
      drawer: 1200,
      modal: 13200,
      snackbar: 13300,
      tooltip: 13400,
    },

    palette: {
      mode: mode === "dark" ? "dark" : "light",
      primary: {
        main: isNil(primaryColor) ? NAVY_MAIN : primaryColor,
        light: NAVY_LIGHT,
        dark: NAVY_DARK,
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: isNil(secondaryColor) ? SLATE_MAIN : secondaryColor,
        light: SLATE_LIGHT,
        dark: SLATE_DARK,
        contrastText: "#FFFFFF",
      },
      amber: {
        main: AMBER_MAIN,
        light: AMBER_LIGHT,
        dark: AMBER_DARK,
        contrastText: "#FFFFFF",
      },
      success: {
        main: STATUS.success.main,
        light: STATUS.success.fill,
        dark: STATUS.success.main,
        contrastText: "#ffffff",
        fill: STATUS.success.fill,
        border: STATUS.success.border,
      },
      warning: {
        main: STATUS.warning.main,
        light: STATUS.warning.fill,
        dark: STATUS.warning.main,
        contrastText: "#ffffff",
        fill: STATUS.warning.fill,
        border: STATUS.warning.border,
      },
      error: {
        main: STATUS.error.main,
        light: STATUS.error.fill,
        dark: STATUS.error.main,
        contrastText: "#ffffff",
        fill: STATUS.error.fill,
        border: STATUS.error.border,
      },
      info: {
        main: STATUS.info.main,
        light: STATUS.info.fill,
        dark: STATUS.info.main,
        contrastText: "#ffffff",
        fill: STATUS.info.fill,
        border: STATUS.info.border,
      },

      // Custom keys consumed across the app.
      divider: LINE,
      dividerLight: LINE_SOFT,
      separator: LINE,

      background: {
        default: CANVAS,
        paper: SURFACE,
        secondary: CANVAS,
        canvas: CANVAS,
      },
      text: {
        primary: INK,
        secondary: MUTED,
        disabled: FAINT,
        title: NAVY_MAIN,
      },
    },

    typography: {
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      h1: {
        fontFamily: FONT_FAMILY,
        fontSize: "1.875rem",
        fontWeight: 700,
        lineHeight: 1.1,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontFamily: FONT_FAMILY,
        fontSize: "1.25rem",
        fontWeight: 700,
        lineHeight: 1.25,
      },
      h3: {
        fontFamily: FONT_FAMILY,
        fontSize: "1rem",
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h4: {
        fontFamily: FONT_FAMILY,
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontFamily: FONT_FAMILY,
        fontSize: "0.8125rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontFamily: FONT_FAMILY,
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: { fontSize: "0.875rem", lineHeight: 1.55 },
      body2: { fontSize: "0.8125rem", lineHeight: 1.5 },
      subtitle1: { fontSize: "0.84375rem", fontWeight: 600, lineHeight: 1.5 },
      subtitle2: { fontSize: "0.75rem", fontWeight: 600, lineHeight: 1.4 },
      caption: { fontSize: "0.71875rem", fontWeight: 500, lineHeight: 1.4 },
      overline: {
        fontSize: "0.6875rem",
        fontWeight: 700,
        letterSpacing: "0.7px",
        textTransform: "uppercase",
        lineHeight: 1.3,
      },
      button: {
        fontSize: "0.84375rem",
        fontWeight: 500,
        textTransform: "none",
      },
    },

    components: {
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${LINE}`,
          },
          elevation1: { boxShadow: MODAL_SHADOW },
          elevation8: { boxShadow: MODAL_SHADOW },
        },
      },

      MuiCard: {
        variants: [
          {
            props: { suppressPadding: true },
            style: { padding: 0 },
          },
          {
            props: { variant: "outlined" },
            style: { boxShadow: CARD_SHADOW },
          },
          {
            props: { variant: "elevated" },
            style: {
              boxShadow: "0 12px 24px #0000000a",
              borderRadius: CARD_RADIUS,
              border: `1px solid ${LINE}`,
              backgroundColor: SURFACE,
            },
          },
        ],
        styleOverrides: {
          root: {
            background: SURFACE,
            padding: "20px",
            borderRadius: CARD_RADIUS,
            border: `1px solid ${LINE}`,
            boxShadow: CARD_SHADOW,
            backgroundImage: "none",
            overflow: "hidden",
          },
        },
      },

      // Buttons default to navy primary, contained, no elevation.
      MuiButton: {
        variants: [
          {
            props: { size: "xs" },
            style: { padding: "2px 8px", minWidth: "30px" },
          },
          // Navy outlined (true "secondary" emphasis)
          {
            props: { variant: "outlined", color: "primary" },
            style: {
              borderWidth: 1.5,
              "&:hover": {
                borderWidth: 1.5,
                backgroundColor: "rgba(27,58,75,0.04)",
              },
            },
          },
          // Amber contained accent
          {
            props: { variant: "contained", color: "amber" },
            style: {
              color: "#FFFFFF",
              "&:hover": { backgroundColor: AMBER_DARK },
            },
          },
          {
            props: { variant: "contained", color: "gray" },
            style: {
              backgroundColor: "#f2f2f2",
              border: `1px solid ${grey[300]}`,
              color: INK,
              "&:hover": { backgroundColor: "#e2e6eb" },
            },
          },
          {
            props: { variant: "outlined", color: "gray" },
            style: {
              borderColor: LINE,
              color: MUTED,
              "&:hover": {
                borderColor: LINE_HOVER,
                backgroundColor: "rgba(100,116,139,0.04)",
              },
            },
          },
          {
            props: { variant: "text", color: "gray" },
            style: {
              color: MUTED,
              "&:hover": { backgroundColor: "rgba(100,116,139,0.06)" },
            },
          },
          // Ghost: a low-emphasis white button with a soft border + hairline
          // shadow (slate text). Matches the ".cnd-btn-ghost" pill used for
          // secondary actions like "Select" / "Email conditions".
          {
            props: { variant: "ghost" },
            style: {
              ...PILL_BUTTON_SIZING,
              color: "#334155",
              backgroundColor: "#ffffff",
              border: "1px solid #dbe1e8",
              boxShadow: "0 1px 1.5px rgba(15, 23, 42, 0.05)",
              "&:hover": {
                backgroundColor: "#f8fafc",
                borderColor: LINE_HOVER,
                boxShadow: "0 1px 1.5px rgba(15, 23, 42, 0.05)",
              },
              "&.Mui-disabled": {
                color: FAINT,
                backgroundColor: "#ffffff",
                borderColor: LINE,
                boxShadow: "none",
              },
            },
          },
          // Solid: the navy primary fill in the SAME compact pill sizing as
          // `ghost`, so a filled primary action ("Add condition") lines up
          // exactly with the ghost buttons beside it. The transparent 1px border
          // keeps its box dimensions identical to ghost's bordered box.
          {
            props: { variant: "solid" },
            style: {
              ...PILL_BUTTON_SIZING,
              color: "#ffffff",
              backgroundColor: NAVY_MAIN,
              border: "1px solid transparent",
              boxShadow: "0 1px 1.5px rgba(15, 23, 42, 0.05)",
              "&:hover": {
                backgroundColor: NAVY_DARK,
                boxShadow: "0 1px 1.5px rgba(15, 23, 42, 0.05)",
              },
              "&.Mui-disabled": {
                color: "#ffffff",
                backgroundColor: FAINT,
                borderColor: "transparent",
                boxShadow: "none",
              },
            },
          },
        ],
        defaultProps: {
          variant: "contained",
          color: "primary",
          size: "small",
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            flexShrink: 0,
            fontWeight: 500,
            textTransform: "none",
            borderRadius: CONTROL_RADIUS,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          },
          // Roomier default (medium) buttons per spec; size="small" stays compact.
          sizeMedium: { paddingInline: 18, paddingBlock: 9 },
        },
      },

      // App bar uses the teal brand color.
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          colorPrimary: {
            backgroundColor: TEAL_BRAND,
            color: "#FFFFFF",
          },
        },
      },

      MuiInputBase: {
        defaultProps: { ...inputDefaultProps },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: CONTROL_RADIUS,
            fontSize: "0.84375rem",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: LINE },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: LINE_HOVER,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: TEAL_BRAND,
              borderWidth: 1,
            },
            "&.Mui-focused": { boxShadow: FOCUS_RING },
          },
        },
      },

      MuiTextField: {
        defaultProps: { ...inputDefaultProps },
        variants: [
          {
            props: { variant: "ghost" },
            style: {
              "& .MuiInputLabel-root": {
                color: GHOST_PLACEHOLDER,
                "&.Mui-focused": { color: GHOST_COLOR },
                "&.Mui-error": { color: GHOST_COLOR },
                "&.Mui-disabled": { color: GHOST_DISABLED },
              },
              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: GHOST_BORDER,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: GHOST_BORDER_HOVER,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: GHOST_BORDER_FOCUS,
                },
                "&.Mui-focused": { boxShadow: "none" },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: GHOST_COLOR,
                },
                "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255, 255, 255, 0.25)",
                },
                "& .MuiOutlinedInput-input": {
                  color: GHOST_COLOR,
                  "&::placeholder": { color: GHOST_PLACEHOLDER, opacity: 1 },
                },
                "& .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                  color: GHOST_COLOR,
                },
              },
              "& .MuiSelect-select": { color: GHOST_COLOR },
              "& .MuiSelect-icon": { color: GHOST_COLOR },
              "& .MuiFormHelperText-root": {
                color: GHOST_PLACEHOLDER,
                "&.Mui-error": { color: GHOST_COLOR },
                "&.Mui-disabled": { color: GHOST_DISABLED },
              },
            },
          },
        ],
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "0.84375rem",
            // Resting (non-floated) labels double as the field's placeholder, so
            // keep them light + muted instead of the bold/dark MuiFormLabel
            // default. Floated (shrunk) labels keep their emphasis.
            "&:not(.MuiInputLabel-shrink)": {
              fontWeight: 400,
              color: MUTED,
            },
          },
        },
      },

      MuiFormLabel: {
        styleOverrides: {
          root: { fontSize: "0.75rem", fontWeight: 600, color: INK },
        },
      },

      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: "0.71875rem",
            opacity: 0.9,
            marginLeft: 0,
            "&.Mui-error": { marginLeft: 0 },
          },
        },
      },

      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: MUTED,
            "& .MuiSvgIcon-root": { fontSize: "15px", color: MUTED },
          },
        },
      },

      MuiTooltip: {
        defaultProps: {
          placement: "top",
          arrow: true,
          disableInteractive: true,
        },
        styleOverrides: {
          tooltip: {
            fontSize: "12px",
            padding: "8px",
            borderRadius: 8,
            backgroundColor: INK,
          },
          arrow: { color: INK },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: { textTransform: "none", borderRadius: CONTROL_RADIUS },
        },
      },

      MuiTabs: {
        styleOverrides: { root: { minHeight: "26px" } },
      },

      // Status chips
      MuiChip: {
        variants: [
          {
            props: { color: "amber" },
            style: {
              backgroundColor: AMBER_LIGHT,
              color: AMBER_CHIP_TEXT,
            },
          },
        ],
        styleOverrides: {
          root: { borderRadius: 999, fontWeight: 600 },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: CARD_RADIUS,
            border: `1px solid ${LINE}`,
            boxShadow: MODAL_SHADOW,
          },
          root: {
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(71,85,105,.5)",
              backdropFilter: "blur(4px)",
            },
          },
        },
      },
    },
  });
};

export default generateMuiThemeDefaults;
